/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import {
  useState,
  useMemo,
  useEffect,
  useCallback,
  KeyboardEventHandler,
} from 'react';
import { RIGHT_KEY, LEFT_KEY, TAB_KEY } from '../../../constants';

type useHeaderArrowNavProps = {
  disableNextMonth?: boolean;
  disablePrevMonth?: boolean;
  refs: React.RefObject<HTMLButtonElement>[];
  firstIdx: number;
  lastIdx: number;
  delta: number;
};

export default function useHeaderArrowNav({
  disableNextMonth = false,
  disablePrevMonth = false,
  refs,
  firstIdx,
  lastIdx,
  delta,
}: useHeaderArrowNavProps) {
  const [enableNavKeys, setEnableNavKeys] = useState(false);
  const [focusedItemIdx, setFocusedItemIdx] = useState(-1);

  const updateFocusedItem = useCallback((index: number) => {
    setFocusedItemIdx(index);
    setEnableNavKeys(true);
  }, []);

  const clearFocusedItems = useCallback(() => {
    setEnableNavKeys(false);
  }, []);

  const keyHandlerMap = useMemo(
    () => ({
      [String(LEFT_KEY)]: () => {
        setFocusedItemIdx(Math.max(focusedItemIdx - delta, firstIdx));
      },
      [String(RIGHT_KEY)]: () => {
        setFocusedItemIdx(Math.min(focusedItemIdx + delta, lastIdx));
      },
    }),
    [focusedItemIdx, lastIdx, firstIdx, delta],
  );

  const handleKeyDown = useCallback<KeyboardEventHandler>(
    (event) => {
      if (disableNextMonth && disablePrevMonth) return;
      if (enableNavKeys) {
        const { key } = event;
        if (keyHandlerMap[key]) {
          event.preventDefault();
          keyHandlerMap[key]();
        } else if (key === String(TAB_KEY)) {
          setEnableNavKeys(false);
        }
      }
    },
    [enableNavKeys, keyHandlerMap, disableNextMonth, disablePrevMonth],
  );

  useEffect(() => {
    if (enableNavKeys) {
      if (refs[focusedItemIdx] && refs[focusedItemIdx].current) {
        refs[focusedItemIdx].current?.focus();
      }
    }
  }, [enableNavKeys, focusedItemIdx, refs]);

  return {
    updateFocusedItem,
    clearFocusedItems,
    handleKeyDown,
  };
}
